import { concat, fromBase64 } from '../bytes/encoding';
import { ParseError } from '../bytes/errors';
import { parseDigitallySigned, u8, u64, writeDigitallySigned } from '../tls/vectors';
import { verifyDigitallySigned, type PublicKey, type VerifyResult } from '../crypto/sig';
import { CT_V1, SignatureType, type SignedTreeHead } from './types';

/** RFC 6962 §3.5 — the 50-byte TreeHeadSignature input. */
export function treeHeadSignatureInput(timestamp: number, treeSize: number, rootHash: Uint8Array): Uint8Array {
  if (rootHash.length !== 32) throw new RangeError('root hash must be 32 bytes');
  return concat(u8(CT_V1), u8(SignatureType.tree_hash), u64(timestamp), u64(treeSize), rootHash);
}

export interface SthJson {
  tree_size: number;
  timestamp: number;
  sha256_root_hash: string;
  tree_head_signature: string;
}

export function parseSthJson(json: unknown): SignedTreeHead {
  const j = json as Partial<SthJson>;
  if (
    typeof j?.tree_size !== 'number' ||
    typeof j.timestamp !== 'number' ||
    typeof j.sha256_root_hash !== 'string' ||
    typeof j.tree_head_signature !== 'string'
  ) {
    throw new ParseError('get-sth: missing or malformed fields');
  }
  const rootHash = fromBase64(j.sha256_root_hash);
  if (rootHash.length !== 32) throw new ParseError('get-sth: sha256_root_hash is not 32 bytes');
  return {
    treeSize: j.tree_size,
    timestamp: j.timestamp,
    rootHash,
    signature: parseDigitallySigned(fromBase64(j.tree_head_signature)),
  };
}

export function verifySth(sth: SignedTreeHead, key: PublicKey): Promise<VerifyResult> {
  return verifyDigitallySigned(key, sth.signature, treeHeadSignatureInput(sth.timestamp, sth.treeSize, sth.rootHash));
}

/** Re-encodes a tree head as its RFC 6962 signature blob (useful to display or export). */
export function encodeTreeHeadSignature(sth: SignedTreeHead): Uint8Array {
  return writeDigitallySigned(sth.signature);
}
